import { React, PropTypes } from 'imports';
import Enum from 'utils/Enum';
import './ScrollBox.less';

const globalScroll = 0;
const beforeListeners = [];
const listeners = [];
function updateGlobalScroll() {
    // get scroll from DOM only once in case of multiple <ScrollBox global/> to prevent unnesessary reflows
    const scrollTop = (document.scrollingElement || document.documentElement).scrollTop;
    beforeListeners.forEach(l => l(scrollTop));
    (window.requestAnimationFrame || setTimeout)(() => listeners.forEach(l => l(scrollTop)));
}
let attached = false;

export const Anchor = Enum('AUTO', 'TOP', 'CHILD_INDEX', 'BOTTOM');

export default class ScrollBox extends React.Component {
    static get Anchor() {
        return Anchor;
    }

    static get propTypes() {
        return {
            global: PropTypes.bool,
            tagName: PropTypes.string,
            className: PropTypes.string,
            style: PropTypes.object,
            anchor: PropTypes.oneOf(Object.values(Anchor)),
            position: PropTypes.number,
            // actually it is called on the same event as onScroll
            // but it is guaranteed to be called before any related onScroll callbacks
            // so if you need to fetch some calculated sizes from DOM
            // this is the place and time to do it preventing unnecessary reflows
            onBeforeScroll: PropTypes.func,
            onScroll: PropTypes.func,
            onScrolledToBottom: PropTypes.func
        }
    }

    static get defaultProps() {
        return {
            tagName: 'div',
            className: '',
            anchor: Anchor.AUTO,
            position: 0
        }
    }

    _offsetTop = 0;
    _scrollTop = 0;
    _postRenderScrollUpdateTimeoutID = null;
    _postMountOverflowUpdateTimeoutID = null;

    _updateScroll() {
        if (this._postRenderScrollUpdateTimeoutID) {
            clearTimeout(this._postRenderScrollUpdateTimeoutID);
            this._postRenderScrollUpdateTimeoutID = null;
        }

        if (this.props.global) {
            this._offsetTop = 0;
            let node = this.refs.root;
            while (node && node !== document.documentElement) {
                this._offsetTop += node.offsetTop;
                node = node.offsetParent;
            }
        }

        if (this.props.anchor === Anchor.AUTO) {
            return;
        }

        const root = !this.props.global ? this.refs.root : document.scrollingElement || document.documentElement;
        const oldScrollTop = !this.props.global ? root.scrollTop : globalScroll;
        let newScrollTop = oldScrollTop;
        if (this.props.anchor === Anchor.TOP) {
            newScrollTop = this.props.position;
        }
        else if (this.props.anchor === Anchor.CHILD_INDEX) {
            const child = root.childNodes[this.props.position];
            const dt = oldScrollTop - child.offsetTop;
            const dh = root.offsetHeight - child.offsetHeight;
            const s = dh < 0 ? -1 : 1;
            newScrollTop -= s * Math.max(s * dt, Math.min(0, s * (dt + dh)));
        }
        else if (this.props.anchor === Anchor.BOTTOM) {
            newScrollTop = root.scrollHeight - root.offsetHeight - this.props.position + this._offsetTop;
        }
        if (oldScrollTop !== newScrollTop) {
            root.scrollTop = this._scrollTop = newScrollTop;
        }
    }

    componentWillMount() {
        if (!attached) {
            window.addEventListener('scroll', updateGlobalScroll);
            attached = true;
        }

        if (this.props.global) {
            beforeListeners.push(this._onBeforeGlobalScroll);
            listeners.push(this._onGlobalScroll);
        }
    }

    componentDidMount() {
        if (!this.props.global) {
            // sometimes chrome gets to infinite loop trying to calculate sizes of scrollable div inside element with display table-cell
            // but first render with visible overflow and then changing it seems to fix it somehow
            this._postMountOverflowUpdateTimeoutID = setTimeout(() => {
                this.refs.root.style.overflowY = 'auto';
                this._postMountOverflowUpdateTimeoutID = null;
            });
        }
        this.componentDidUpdate();
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.global !== nextProps.global) {
            throw Error('Can not change prop "global" of an existing ScrollBox', this);
        }
    }

    componentDidUpdate() {
        // React seems to update DOM and call componentDidUpdate in order of tree traversal
        // so this method might happen before some other component update
        // _updateScroll does not change layout, but it calculates new component sizes and probably changes scroll position
        // calculating sizes causes forced reflow and if it happens in the middle of render thread it might reduce performance significantly
        // it should be pretty safe (and maybe even more accurate for global=true) to call it after the whole render thread is complete
        // though it also might update position with noticeable delay
        // currently we don't manipulate scroll position anywhere, ScrollBoxes are used only to track position, but something TODO
        this._postRenderScrollUpdateTimeoutID = setTimeout(() => this._updateScroll());
    }

    componentWillUnmount() {
        if (this.props.global) {
            beforeListeners.splice(beforeListeners.indexOf(this._onBeforeGlobalScroll), 1);
            listeners.splice(listeners.indexOf(this._onGlobalScroll), 1);
        }
        if (this._postMountOverflowUpdateTimeoutID) {
            clearTimeout(this._postMountOverflowUpdateTimeoutID);
            this._postMountOverflowUpdateTimeoutID = null;
        }
        if (this._postRenderScrollUpdateTimeoutID) {
            clearTimeout(this._postRenderScrollUpdateTimeoutID);
            this._postRenderScrollUpdateTimeoutID = null;
        }
    }

    _onScrollToBottomDetect = () => {
        if (this.props.onScrolledToBottom && this.refs.root.offsetHeight + this.refs.root.scrollTop >= this.refs.root.scrollHeight) {
            if (!this._justScrolledToBottomTimeoutID) {
                this.props.onScrolledToBottom();
            } else {
                clearTimeout(this._justScrolledToBottomTimeoutID);
            }
            this._justScrolledToBottomTimeoutID = setTimeout(() => {
                this._justScrolledToBottomTimeoutID = null;
            }, 500);
        }
    }

    _onSelfScroll = (scrollTop) => {
        scrollTop = this.refs.root.scrollTop;
        if (this.props.onBeforeScroll) {
            // global scrollboxes have common event and their onBeforeScroll handlers are called separately before any onScroll
            // but local has it's own scrollable element and therefore only one handler per event so it should be called here
            this.props.onBeforeScroll(this, Anchor.TOP, scrollTop - this._offsetTop, scrollTop - this._scrollTop);
        }
        if (this.props.onScroll && scrollTop !== this._scrollTop) {
            this.props.onScroll(this, Anchor.TOP, scrollTop - this._offsetTop);
        }
        this._scrollTop = scrollTop;
        this._onScrollToBottomDetect();
    }

    _onBeforeGlobalScroll = (scrollTop) => {
        if (this.props.onBeforeScroll) {
            this.props.onBeforeScroll(this, Anchor.TOP, scrollTop - this._offsetTop, scrollTop - this._scrollTop);
        }
    }

    _onGlobalScroll = (scrollTop) => {
        if (this.props.onScroll) {
            this.props.onScroll(this, Anchor.TOP, scrollTop - this._offsetTop, scrollTop - this._scrollTop);
        }
        this._scrollTop = scrollTop;
        // this._onScrollToBottomDetect(); // not used anywhere yet, let's save couple of milliseconds per scroll event
    }

    render() {
        return (
            <this.props.tagName ref="root" className={`scroll-box ${!this.props.global ? 'in-page' : 'global'} ${this.props.className}`} style={this.props.style} onScroll={!this.props.global ? this._onSelfScroll : null}>
                {this.props.children}
            </this.props.tagName>
        );
    }
}
